<template>
    <div >
       <div class="chart-container">
        <canvas ref="activityChart"></canvas>
       </div>
       
         
            <p class="text-center" style="margin-top: 10px;">
                Activities Breakdown <br />
                Completed: {{ totalCompleted }} | Incomplete: {{ totalIncomplete }}
            </p>
       
    </div>
    
</template>

<script>
import Chart from 'chart.js';

export default {
    name: 'ConcentricPieChartsWithGap',
    props: {
        activities: {
            type: Object,
            required: true,
            default: () => ({
                completed: {},
                incomplete: {}
            })
        }
    },
    computed: {
        totalCompleted() {
            if (!this.activities || !this.activities.activities.completed) {
                return 0;
            }
            return Object.values(this.activities.activities.completed).reduce((sum, count) => sum + count, 0);
        },
        totalIncomplete() {
            if (!this.activities || !this.activities.activities.incomplete) {
                return 0;
            }
            return Object.values(this.activities.activities.incomplete).reduce((sum, count) => sum + count, 0);
        }
    },
    mounted() {
        this.renderChart();
    },
    watch: {
        activities: {
            immediate: true,
            handler(newActivities) {
                this.renderChart();
            }
        }
    },
    methods: {
        renderChart() {
        const ctx = this.$refs.activityChart?.getContext('2d');
        if (!ctx) return;

        // Destroy any existing chart to avoid duplication
        if (this.chart) {
            this.chart.destroy();
        }

        // Validate the structure of activities data
        if (!this.activities || !this.activities.activities || !this.activities.activities.completed || !this.activities.activities.incomplete) {
            console.warn("Invalid activities data structure");
            return;
        }

        // Prepare data for completed activities
        const completedLabels = Object.keys(this.activities.activities.completed);
        const completedData = Object.values(this.activities.activities.completed).filter(value => value !== null);

        // Prepare data for incomplete activities
        const incompleteLabels = Object.keys(this.activities.activities.incomplete);
        const incompleteData = Object.values(this.activities.activities.incomplete).filter(value => value !== null);

        if (completedData.length === 0 && incompleteData.length === 0) {
            console.warn("No valid data for chart rendering");
            return;
        }

        // Predefined colors
        const predefinedColors = [
            '#4caf50', '#8bc34a', '#cddc39',
            '#fb7289', '#ffb74d', '#ffeb3b',
            '#2196F3', '#03A9F4', '#00BCD4',
            '#9C27B0', '#673AB7', '#3F51B5'
        ];

        const colorMap = {};

        // Assign colors to completed activities
        completedLabels.forEach((label, index) => {
            colorMap[label] = predefinedColors[index % predefinedColors.length];
        });

        // Assign colors to incomplete activities
        incompleteLabels.forEach((label, index) => {
            colorMap[label] = predefinedColors[(completedLabels.length + index) % predefinedColors.length];
        });

        // Create datasets for the chart
        const completedColors = completedLabels.map(label => colorMap[label]);
        const incompleteColors = incompleteLabels.map(label => colorMap[label]);

        // Create the doughnut chart with two rings
        this.chart = new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels: completedLabels.concat(incompleteLabels), // Combine for display
                datasets: [
                    {
                        label: 'Completed Activities',
                        data: completedData,
                        backgroundColor: completedColors,
                        borderColor: '#fff',
                        borderWidth: 2
                    },
                    {
                        label: 'Incomplete Activities',
                        data: incompleteData,
                        backgroundColor: incompleteColors,
                        borderColor: '#fff',
                        borderWidth: 2
                    }
                ]
            },
            options: {
                responsive: true,
                cutoutPercentage: 60,
                rotation: 0,
                circumference: 2 * Math.PI,
                tooltips: {
                    enabled: true,
                    mode: 'index',
                    callbacks: {
                        label: function (tooltipItem, data) {
                            const label = data.labels[tooltipItem.index] || '';
                            const value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
                            return `${label}: ${value}`;
                        }
                    }
                },
                legend: {
                    display: true,
                    position: 'right', // Move legend to the right
                    labels: {
                        generateLabels: function(chart) {
                            const data = chart.data;
                            const completedLegends = completedLabels.map((label, index) => ({
                                text: `${label} (${data.datasets[0].data[index]})`,
                                fillStyle: data.datasets[0].backgroundColor[index],
                                hidden: false,
                                datasetIndex: 0,
                            }));

                            const incompleteLegends = incompleteLabels.map((label, index) => ({
                                text: `${label} (${data.datasets[1].data[index]})`,
                                fillStyle: data.datasets[1].backgroundColor[index],
                                hidden: false,
                                datasetIndex: 1,
                            }));

                            // Generate combined legends for table-like structure
                            const maxRows = Math.max(completedLegends.length, incompleteLegends.length);
                            const legendRows = [];

                            for (let i = 0; i < maxRows; i++) {
                                const completedItem = completedLegends[i] || { text: '' };
                                const incompleteItem = incompleteLegends[i] || { text: '' };
                                legendRows.push({
                                    text: `${completedItem.text} | ${incompleteItem.text}`,
                                    fillStyle: completedItem.fillStyle, // Keeping the first color (could be modified)
                                    hidden: false,
                                });
                            }

                            return [
                               
                                ...legendRows,
                               
                            ];
                        },
                    }
                }
            }
        });
    }
    }
};
</script>

<style>
.chart-container {
    display: flex; /* Use flexbox for layout */
    align-items: center; /* Center align items vertically */
}

canvas {
    height: 300px;
    width: 300px;
}
</style>
